import { cn } from '@/utils/utils.ts';
import { format } from 'date-fns';
import { calculateDateDifference } from '@/utils/compare-dates.ts';

import { ColumnDef } from '@tanstack/react-table';

import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { EventRulesTableColumnHeader } from './event-rules-table-column-header.tsx';
import { EventRulesTableRowActions } from './event-rules-table-row-actions.tsx';
import { DeviceTypeNames } from '@/features/devices/schemas/devices.schemas.ts';
import {
  EventRule,
  eventRuleStatuses,
} from '@/features/event-rules/schemas/event-rules.schema.ts';

const getLabelDevice = (deviceType: DeviceTypeNames) => {
  if (deviceType === 'Inverter') {
    return 'blue'; // Primary device
  }
  if (deviceType === 'BMS') {
    return 'purple'; // Critical management system
  }
  if (deviceType === 'SmartMeter') {
    return 'green'; // Measurement device
  }
  if (deviceType === 'SmartLogger') {
    return 'indigo'; // Data logging device
  }
  if (deviceType === 'Dongle') {
    return 'pink'; // Communication device
  }
  if (deviceType === 'Logger') {
    return 'yellow'; // Basic logging device
  }
  if (deviceType === 'PQConnect') {
    return 'red'; // Power quality device
  }
  if (deviceType === 'Battery') {
    return 'blue'; // Energy storage
  }
  if (deviceType === 'Unknown') {
    return 'gray'; // Unknown device
  }
  return 'gray'; // default fallback
};

export const eventRulesTableColumns: ColumnDef<EventRule>[] = [
  {
    size: 32,
    id: 'select',
    header: ({ table }) => (
      <Checkbox
        checked={
          table.getIsAllPageRowsSelected() ||
          (table.getIsSomePageRowsSelected() && 'indeterminate')
        }
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        aria-label="Select all"
        className="translate-y-[2px]"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        aria-label="Select row"
        className="translate-y-[2px]"
      />
    ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: 'eventTitle',
    header: ({ column }) => <EventRulesTableColumnHeader column={column} title="Title" />,
    cell: ({ row }) => {
      return <span className="max-w-[300px] truncate">{row.getValue('eventTitle')}</span>;
    },
  },
  {
    accessorKey: 'status',
    header: ({ column }) => <EventRulesTableColumnHeader column={column} title="Status" />,
    cell: ({ row }) => {
      const status = eventRuleStatuses.find(
        (status) => status.value === row.getValue('status')
      );

      if (!status) {
        return null;
      }

      return (
        <div className="flex w-[100px] items-center">
          <Badge variant={status.value === 'enabled' ? 'green' : 'red'}>{status.label}</Badge>
        </div>
      );
    },
    filterFn: (row, id, value) => {
      return value.includes(row.getValue(id));
    },
  },
  {
    accessorKey: 'plantName',
    header: ({ column }) => <EventRulesTableColumnHeader column={column} title="Plant" />,
    cell: ({ row }) => (
      <div className="max-w-[150px] truncate">{row.getValue('plantName')}</div>
    ),
  },
  {
    accessorKey: 'deviceType',
    header: ({ column }) => (
      <EventRulesTableColumnHeader column={column} title="Device Type" />
    ),
    cell: ({ row }) => {
      const deviceType = row.getValue('deviceType') as DeviceTypeNames;

      return deviceType ? (
        <Badge variant={getLabelDevice(deviceType)} className="capitalize">
          {deviceType}
        </Badge>
      ) : (
        <span className="text-muted-foreground">-</span>
      );
    },
    filterFn: (row, id, value) => {
      return value.includes(row.getValue(id));
    },
  },
  {
    accessorKey: 'conditions',
    header: ({ column }) => <EventRulesTableColumnHeader column={column} title="Conditions" />,
    cell: ({ row }) => {
      const conditions = row.getValue('conditions') as Array<{
        parameter: string;
        operator: string;
        value: string;
      }>;

      return (
        <div className="max-w-[200px]">
          <Badge variant="gray">
            {conditions.length} condition{conditions.length !== 1 ? 's' : ''}
          </Badge>
        </div>
      );
    },
    enableSorting: false,
  },
  {
    accessorKey: 'cooldownPeriod',
    header: ({ column }) => (
      <EventRulesTableColumnHeader column={column} title="Cooldown (min)" />
    ),
    cell: ({ row }) => {
      const cooldown = row.getValue('cooldownPeriod') as number;
      return <div className="font-mono">{cooldown.toLocaleString()}</div>;
    },
  },
  {
    accessorKey: 'triggerCount',
    header: ({ column }) => <EventRulesTableColumnHeader column={column} title="Triggers" />,
    cell: ({ row }) => {
      const count = row.getValue('triggerCount') as number;
      return <div className="font-mono">{count?.toLocaleString() || 0}</div>;
    },
  },
  {
    accessorKey: 'lastTriggeredAt',
    header: ({ column }) => (
      <EventRulesTableColumnHeader column={column} title="Last Triggered" />
    ),
    cell: ({ row }) => {
      const lastTriggeredAt = row.getValue('lastTriggeredAt') as string;
      let label = '';
      let color = '';

      if (lastTriggeredAt) {
        const { days: daysDifference } = calculateDateDifference(
          new Date(lastTriggeredAt),
          new Date()
        );

        label = format(new Date(lastTriggeredAt), 'MMM dd, yyyy HH:mm');
        color = daysDifference < 7 ? 'text-green-500' : 'text-orange-500';
      }

      return lastTriggeredAt ? (
        <div className="flex space-x-2 text-nowrap">
          <span className={cn('max-w-[500px] font-medium', color)}>{label}</span>
        </div>
      ) : (
        <span className="text-muted-foreground">Never</span>
      );
    },
  },
  {
    size: 10,
    id: 'actions',
    cell: ({ row }) => <EventRulesTableRowActions row={row} />,
  },
];
